import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export default function AdminReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [services, setServices] = useState([]);
  const [sites, setSites] = useState([]);
  const now = new Date();
  const [filters, setFilters] = useState({
    period_type: 'month',
    month: now.toISOString().slice(0, 7),
    quarter: `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`,
    year: String(now.getFullYear()),
    service_id: '',
    site_id: '',
  });

  const buildParams = () => {
    const p = {};
    if (filters.period_type === 'month' && filters.month) p.month = filters.month;
    if (filters.period_type === 'quarter' && filters.quarter) p.quarter = filters.quarter;
    if (filters.period_type === 'year' && filters.year) p.year = filters.year;
    if (filters.service_id) p.service_id = filters.service_id;
    if (filters.site_id) p.site_id = filters.site_id;
    return new URLSearchParams(p).toString();
  };

  useEffect(() => {
    api.get('/admin/services').then((r) => setServices(r.data?.data || [])).catch(() => {});
    api.get('/admin/sites').then((r) => setSites(r.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(`/admin/reports/global?${buildParams()}`)
      .then((res) => {
        setData(res.data?.data ?? res.data);
        setError(null);
      })
      .catch((err) => {
        setData(null);
        const msg = err.response?.data?.message
          || err.response?.data?.error
          || (err.response?.status === 403 ? 'Accès refusé (rôle insuffisant)' : null)
          || (err.response?.status === 404 ? 'Rapport non trouvé' : null)
          || err.message
          || 'Erreur réseau ou serveur';
        setError(msg);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.period_type, filters.month, filters.quarter, filters.year, filters.service_id, filters.site_id]);

  if (loading) return <p className="text-gray-500">Chargement...</p>;
  if (error || !data) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-gray-800">Rapport global</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <p className="font-medium">Erreur lors du chargement</p>
          <p className="text-sm mt-1">{error || 'Impossible de récupérer les données.'}</p>
          <p className="text-xs mt-2 text-red-600">
            Vérifiez que l&apos;API est démarrée (php artisan serve) et que vous êtes connecté avec un compte ayant le rôle admin, rh ou direction.
          </p>
        </div>
      </div>
    );
  }

  const { indicators, comparison_n1, executive, trend } = data;
  const execLabels = { green: 'Stable', yellow: 'En baisse', red: 'Élevé', gray: '—' };
  const execEmoji = { green: '🟢', yellow: '🟡', red: '🔴', gray: '⚪' };

  const exportPdf = async () => {
    try {
      const r = await api.get(`/admin/reports/global/pdf?${buildParams()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rapport-global.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  const exportExcel = async () => {
    try {
      const r = await api.get(`/admin/reports/global/excel?${buildParams()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rapport-global.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Rapport global</h1>
        <div className="flex gap-2">
          <button
            onClick={exportPdf}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Export PDF
          </button>
          <button
            onClick={exportExcel}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Export Excel
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Période :</label>
          <select
            value={filters.period_type}
            onChange={(e) => setFilters({ ...filters, period_type: e.target.value })}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          >
            <option value="month">Mois</option>
            <option value="quarter">Trimestre</option>
            <option value="year">Année</option>
          </select>
        </div>
        {filters.period_type === 'month' && (
          <input
            type="month"
            value={filters.month || ''}
            onChange={(e) => setFilters({ ...filters, month: e.target.value })}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          />
        )}
        {filters.period_type === 'quarter' && (
          <select
            value={filters.quarter || ''}
            onChange={(e) => setFilters({ ...filters, quarter: e.target.value })}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          >
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].flatMap((y) =>
              [1, 2, 3, 4].map((q) => (
                <option key={`${y}-Q${q}`} value={`${y}-Q${q}`}>{y} T{q}</option>
              ))
            )}
          </select>
        )}
        {filters.period_type === 'year' && (
          <select
            value={filters.year || ''}
            onChange={(e) => setFilters({ ...filters, year: e.target.value })}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          >
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        )}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Service :</label>
          <select
            value={filters.service_id || ''}
            onChange={(e) => setFilters({ ...filters, service_id: e.target.value })}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm min-w-[140px]"
          >
            <option value="">Tous</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Site :</label>
          <select
            value={filters.site_id || ''}
            onChange={(e) => setFilters({ ...filters, site_id: e.target.value })}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm min-w-[140px]"
          >
            <option value="">Tous</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Vue Executive</h2>
        <table className="min-w-full">
          <tbody>
            <tr className="border-b">
              <td className="py-2 font-medium">Moral global</td>
              <td className="py-2">{execEmoji[executive?.moral_global] ?? '⚪'} {execLabels[executive?.moral_global] ?? '—'}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-medium">Engagement</td>
              <td className="py-2">{execEmoji[executive?.engagement] ?? '⚪'} {execLabels[executive?.engagement] ?? '—'}</td>
            </tr>
            <tr>
              <td className="py-2 font-medium">Risque turnover</td>
              <td className="py-2">{execEmoji[executive?.risque_turnover] ?? '⚪'} {execLabels[executive?.risque_turnover] ?? '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Indice climat</p>
          <p className="text-2xl font-bold text-gray-800">{indicators?.indice_climat ?? '—'}/10</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total participations</p>
          <p className="text-2xl font-bold text-gray-800">{indicators?.total_participations ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Tendance 6 mois</p>
          <p className={`text-2xl font-bold ${(indicators?.tendance_6_mois ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {indicators?.tendance_6_mois !== null ? (indicators.tendance_6_mois >= 0 ? '+' : '') + indicators.tendance_6_mois : '—'}
          </p>
        </div>
      </div>

      {comparison_n1 && comparison_n1.evolution !== null && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-2">Comparaison N-1</h2>
          <p>Évolution : <span className={comparison_n1.evolution >= 0 ? 'text-green-600' : 'text-red-600'}>
            {(comparison_n1.evolution >= 0 ? '+' : '') + comparison_n1.evolution}
          </span> points</p>
        </div>
      )}

      {trend?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4">Tendance des scores</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 10]} />
                <Tooltip formatter={(v) => [v + '/10', 'Score']} />
                <Line type="monotone" dataKey="score" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
